import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  TablePagination,
  Alert,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import CollapsibleCard from "./CollapsibleCard";
import TimePeriodSelector from "../../common/TimePeriodSelector";
import useStationUptime from "src/hooks/useStationUptime.hook";
import { fUTCToLocal } from "src/utils/formatTime";
import { hideColumnsOnPhone } from "./tableSx";

// Charger uptime: how long each charger was connected to the CMS (online) vs
// disconnected (offline), from cms-websocket's connection history via
// GET /team/:teamId/station-uptime. ChargerUptimeBody takes the period from its
// parent (the merged Chargers section); ChargerUptimeV2 is the standalone card.

const PERIOD_TO_RANGE = { Today: "1d", Last7Days: "7d", Last30Days: "30d", Last90Days: "90d" };
export const UPTIME_PERIOD_OPTIONS = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 Days" },
  { value: "Last30Days", label: "Last 30 Days" },
  { value: "Last90Days", label: "Last 90 Days" },
];
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const Tile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "accent",
})(({ theme, accent }) => ({
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderTop: `3px solid ${accent}`,
  boxShadow: theme.customShadows.z1,
  height: "100%",
}));

// 93784 -> "1d 2h", 4320 -> "1h 12m", 300 -> "5m", 40 -> "40s", 0 -> "0m"
export const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return s > 0 ? `${s}s` : "0m";
};

// UTC timestamp from the API -> local time WITH its zone name, e.g. "4 Oct 2026, 10:30 IST",
// so the tooltip is never ambiguous. (Switch to the per-user-timezone formatter once the
// user-timezone feature is on this branch.)
const fOfflineSince = (utc) => {
  const date = fUTCToLocal(utc);
  if (!date) return "-";
  // Explicit fields: Intl throws "Invalid option" if dateStyle/timeStyle are mixed
  // with timeZoneName.
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

const uptimeColor = (theme, uptime) => {
  if (uptime >= 98) return theme.palette.success.main;
  if (uptime >= 90) return theme.palette.warning.main;
  return theme.palette.error.main;
};

// `offlineNow`: the live offline count from the charger list (the merged Chargers
// section passes it so both tabs agree); otherwise counted from connection history.
// `chargepointIds`: when set (a site is selected), only those chargers are shown and
// the totals are recomputed from them; the API's summary covers the whole team.
export const ChargerUptimeBody = ({ teamId, period = "Last7Days", offlineNow: offlineNowProp, chargepointIds = null }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const { data, isLoading, error } = useStationUptime(teamId, PERIOD_TO_RANGE[period]);

  const stations = useMemo(
    // Highest uptime first.
    () =>
      (data?.stations || [])
        .filter((s) => !chargepointIds || chargepointIds.has(s.chargepointId))
        .sort((a, b) => (Number(b.uptime) || 0) - (Number(a.uptime) || 0)),
    [data, chargepointIds]
  );
  const summary = useMemo(() => {
    if (!chargepointIds) return data?.summary || {};
    // Same rule as the API: average over chargers with connection history.
    const withHistory = stations.filter((s) => s.uptimeSource === "connection_history");
    return {
      overallUptime: withHistory.length
        ? withHistory.reduce((n, s) => n + (Number(s.uptime) || 0), 0) / withHistory.length
        : 0,
      totalOfflineSeconds: withHistory.reduce((n, s) => n + (Number(s.offline?.seconds) || 0), 0),
    };
  }, [data, stations, chargepointIds]);
  const offlineNow = offlineNowProp ?? stations.filter((s) => s.offline?.offlineSince).length;
  const disconnections = stations.reduce((n, s) => n + (s.offline?.events || 0), 0);
  const estimated = stations.filter((s) => s.uptimeSource !== "connection_history").length;
  // No charger has connection history for this period: the totals would read 0 /
  // 0.0%, which looks like a measurement. Show "—" instead.
  const hasHistory = stations.length > estimated;
  const noHistoryHint = "No connection history for this period yet";
  // Clamp in case the list shrank (new period) while on a later page.
  const safePage = Math.min(page, Math.max(0, Math.ceil(stations.length / rowsPerPage) - 1));
  const visible = stations.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);

  const tiles = [
    {
      key: "uptime",
      label: "Overall Uptime",
      value: hasHistory ? `${(Number(summary.overallUptime) || 0).toFixed(1)}%` : "—",
      accent: hasHistory ? uptimeColor(theme, Number(summary.overallUptime) || 0) : theme.palette.text.secondary,
      hint: hasHistory ? "Average uptime across chargers in the selected period" : noHistoryHint,
    },
    {
      key: "offline",
      label: "Total Offline Time",
      value: hasHistory ? fmtDuration(summary.totalOfflineSeconds) : "—",
      accent: hasHistory ? theme.palette.error.main : theme.palette.text.secondary,
      hint: hasHistory ? "Combined time chargers were disconnected from the CMS" : noHistoryHint,
    },
    {
      key: "events",
      label: "Disconnections",
      value: hasHistory ? `${disconnections}` : "—",
      accent: hasHistory ? theme.palette.warning.main : theme.palette.text.secondary,
      hint: hasHistory ? "Number of times a charger dropped its connection" : noHistoryHint,
    },
    {
      key: "offlineNow",
      label: "Offline Now",
      value: `${offlineNow}`,
      accent: offlineNow > 0 ? theme.palette.error.main : theme.palette.success.main,
      hint: "Chargers currently disconnected",
    },
  ];

  return error ? (
    <Alert severity="error">Couldn't load charger uptime: {error.message}</Alert>
  ) : (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <Grid item xs={6} md={3} key={t.key}>
            <Tooltip title={t.hint} placement="top">
              <Tile accent={t.accent}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                  {t.label}
                </Typography>
                {isLoading ? (
                  <Box sx={{ py: 0.5 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Typography variant="h5" sx={{ fontWeight: 700, color: t.accent }}>
                    {t.value}
                  </Typography>
                )}
              </Tile>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {!isLoading && estimated > 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {estimated} charger{estimated > 1 ? "s have" : " has"} no connection history for this period yet, so{" "}
          {estimated > 1 ? "their" : "its"} uptime is estimated from the last heartbeat (marked *).
        </Alert>
      )}

      <TableContainer>
        <Table size="small" sx={hideColumnsOnPhone([3, 5, 6])}>
          <TableHead>
            <TableRow>
              <TableCell>Charger</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Uptime</TableCell>
              <TableCell>Online</TableCell>
              <TableCell>Offline</TableCell>
              <TableCell align="right">Disconnections</TableCell>
              <TableCell>Longest Outage</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && stations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No chargers found for this team.
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              visible.map((s) => {
                const uptime = Number(s.uptime) || 0;
                const fromHistory = s.uptimeSource === "connection_history";
                const offlineSince = s.offline?.offlineSince;
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.chargepointId}
                      </Typography>
                      {s.name && s.name !== s.chargepointId && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {s.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, uptime))}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              // Neutral track, so a 0% bar reads as empty, not full.
                              bgcolor: theme.palette.grey[200],
                              "& .MuiLinearProgress-bar": { backgroundColor: uptimeColor(theme, uptime) },
                            }}
                          />
                        </Box>
                        <Tooltip title={fromHistory ? "From connection history" : "Estimated from last heartbeat"}>
                          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 48 }}>
                            {uptime.toFixed(1)}%{fromHistory ? "" : "*"}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>{fromHistory ? fmtDuration(s.online?.seconds) : "—"}</TableCell>
                    <TableCell>{fromHistory ? fmtDuration(s.offline?.seconds) : "—"}</TableCell>
                    <TableCell align="right">{fromHistory ? s.offline?.events || 0 : "—"}</TableCell>
                    <TableCell>{fromHistory ? fmtDuration(s.offline?.longestSeconds) : "—"}</TableCell>
                    <TableCell>
                      {offlineSince ? (
                        <Tooltip title={`Offline since ${fOfflineSince(offlineSince)}`}>
                          <Chip size="small" color="error" variant="outlined" label="Offline" />
                        </Tooltip>
                      ) : s.online?.onlineNow ? (
                        <Chip size="small" color="success" variant="outlined" label="Online" />
                      ) : (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {s.lastIssue && s.lastIssue !== "No recent issues" ? `Last issue ${s.lastIssue}` : s.lastIssue || "—"}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && stations.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={stations.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
    </Box>
  );
};

ChargerUptimeBody.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  period: PropTypes.oneOf(Object.keys(PERIOD_TO_RANGE)),
  offlineNow: PropTypes.number,
  chargepointIds: PropTypes.instanceOf(Set),
};

const ChargerUptimeV2 = ({ teamId }) => {
  const [period, setPeriod] = useState("Last7Days");
  return (
    <CollapsibleCard
      title="Charger Uptime"
      subtitle="How long each charger was connected to the CMS"
      icon={<Icon icon="eva:activity-outline" width={22} height={22} />}
      action={<TimePeriodSelector value={period} onChange={setPeriod} options={UPTIME_PERIOD_OPTIONS} />}
    >
      <ChargerUptimeBody teamId={teamId} period={period} />
    </CollapsibleCard>
  );
};

ChargerUptimeV2.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ChargerUptimeV2;
